import { useEffect, useState } from "react";
import { CyberpassButton } from "../Input";
import toast from "react-hot-toast";
import { formatObject } from "../common/formatting";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import { CrownIcon } from "../features/super/components/icons";
import { useTranslation } from "../i18n";
import { calculateOrder } from "../api/minutesPackage";

/**
 * @param {MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage} p
 * @returns {number}
 */
function getMinutesAmount(p) {
  if ("minutesAmount" in p) return p.minutesAmount;
  if ("package" in p) return getMinutesAmount(p.package);
  return 0;
}

/**
 * @param {MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage} p
 * @param {FlexibleDiscount | null} flexibleDiscount
 * @returns {number}
 */
function getDiscount(p, flexibleDiscount) {
  if (flexibleDiscount != null) return flexibleDiscount.amount;
  if ("discount" in p) return p.discount;
  return 0;
}

/**
 * @param {MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage} p
 * @param {number} [discount]
 * @returns {number}
 */
function getPriceRub(p, discount) {
  if (discount == null) discount = 0;
  const priceRub = "priceRub" in p ? p.priceRub : p.package.priceRub;
  return Math.ceil(priceRub - Number(priceRub * discount));
}

/**
 * @param {MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage} p
 * @returns {number}
 */
function getVipBonus(p) {
  if ("vipBonus" in p) return p.vipBonus;
  if ("package" in p) return getVipBonus(p.package);
  if ("usedPackages" in p) {
    return p.usedPackages.reduce((total, used) => {
      return total + getVipBonus(used.package) * used.amount;
    }, 0);
  }
  return 0;
}

/**
 * Convert the selected packages to the format expected by calculate-order API.
 * CustomMinutesPackage is expanded into its constituent packages.
 *
 * @param {MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage} p
 * @param {number} amount - how many of this package the user selected
 * @returns {import("../global").PackageWithAmount[]}
 */
function toPackageWithAmount(p, amount) {
  if ("id" in p) {
    // Regular MinutesPackage
    return [{ packageId: p.id, amount, discount: null }];
  }
  if ("package" in p && "discount" in p) {
    // MinutesPackageWithDiscount
    return [{ packageId: p.package.id, amount, discount: p.discount }];
  }
  if ("usedPackages" in p) {
    // CustomMinutesPackage — expand into used packages
    return p.usedPackages.map((used) => ({
      packageId: used.package.id,
      amount: used.amount * amount,
      discount: null,
    }));
  }
  return [];
}

/**
 * @param {object} props
 * @param {string} props.accessToken
 * @param {number} props.customerId
 * @param {Array<MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage>} props.customPackages
 * @param {(arg0: CalculateOrderResponse) => void} props.clickConfirm
 * @param {FlexibleDiscount | null} props.flexibleDiscount
 * @param {boolean} [props.isVip]
 * @returns {React.Component}
 */
function ChooseMinutesPackage({
  accessToken,
  customerId,
  customPackages,
  clickConfirm,
  flexibleDiscount,
  isVip,
}) {
  const { t } = useTranslation();
  /** @type {[Array<MinutesPackage | MinutesPackageWithDiscount | CustomMinutesPackage>, any]} */
  const [packages, setPackages] = useState([]);
  const [withDiscount, setWithDiscount] = useState(false);
  useEffect(() => {
    if (customPackages != null) {
      setPackages(customPackages);
      setWithDiscount(customPackages[0] && "discount" in customPackages[0]);
      return;
    }
    console.error("Got empty packages list");
  }, [customPackages]);

  /**
   * @type {[PackageCounter[], (PackageCounter) => void]}
   */
  const [packagesCounter, setPackagesCounter] = useState([]);

  useEffect(() => {
    setPackagesCounter(Array(packages.length).fill({ value: 0 }));
  }, [packages]);

  /**
   * @param {number} packageIdx
   */
  const clickPlus = (packageIdx) => {
    if (
      withDiscount &&
      packagesCounter.reduce((acc, amount) => acc + amount.value, 0) >= 1
    ) {
      toast.error(t("choosePackage.oneDiscountOnly"));
      return;
    }
    let result = packagesCounter.map((v, idx) =>
      idx == packageIdx ? { value: v.value + 1 } : v,
    );
    setPackagesCounter(result);
  };

  /**
   * @param {number} packageIdx
   */
  const clickMinus = (packageIdx) => {
    setPackagesCounter(
      packagesCounter.map((v, idx) =>
        idx === packageIdx ? { ...v, value: Math.max(v.value - 1, 0) } : v,
      ),
    );
  };

  /**
   * Called when user clicks "Подтвердить".
   * Builds the API request from the selected packages and calls calculate-order.
   */
  const handleConfirm = async () => {
    if (packagesCounter.every((c) => c.value === 0)) {
      toast.error(t("choosePackage.selectAtLeastOne"));
      return;
    }

    // Build packagesWithAmount array for the API (expanding CustomMinutesPackage)
    const apiPackagesWithAmount = [];
    // Also keep the raw packages for the buy-minutes submit
    const rawPackages = [];
    packagesCounter.forEach((counter, idx) => {
      if (counter.value === 0) return;
      const p = packages[idx];
      const expanded = toPackageWithAmount(p, counter.value);
      apiPackagesWithAmount.push(...expanded);
      rawPackages.push({ package: p, amount: counter.value });
    });

    try {
      const summary = await calculateOrder(accessToken, {
        customerId,
        packagesWithAmount: apiPackagesWithAmount,
        isVip: isVip ?? false,
        flexibleDiscountId: flexibleDiscount?.id ?? null,
        paymentType: null,
      });
      clickConfirm(summary, rawPackages);
    } catch (err) {
      console.error("calculate-order failed", err);
      toast.error(t("choosePackage.calcFailed"));
    }
  };

  return (
    <div className="space-y-5 flex flex-col items-center bg-c-gray-600 rounded flex-grow h-full">
      <div className="flex justify-center flex-wrap gap-3">
        {packages
          .sort((p1, p2) => getMinutesAmount(p2) - getMinutesAmount(p1))
          .map((p, idx) => {
            const discount = getDiscount(p, flexibleDiscount);

            return (
              <div
                key={JSON.stringify(p)}
                className="bg-c-gray-500 flex flex-col items-center p-4 w-32 rounded-xl relative"
              >
                <div
                  className={`flex flex-col justify-between items-center h-28`}
                >
                  {p.promotesToVip && (
                    <div className="absolute -top-2 -left-2 w-7 aspect-square flex justify-center items-center rounded-full bg-accent">
                      <CrownIcon className="w-4 h-4" fill="#FFFFFF" />
                    </div>
                  )}
                  {discount !== 0 ? (
                    <h3 className="text-xs absolute top-0 right-0 px-2 py-1 rounded-bl-lg rounded-tr-lg bg-accent">
                      {Number(discount * 100)}%
                    </h3>
                  ) : null}
                  <p className="flex flex-row items-end text-gray-300">
                    {isVip && getVipBonus(p) > 0 ? (
                      <span className="text-2xl font-bold text-white mr-1">
                        {getMinutesAmount(p) + getVipBonus(p)}
                      </span>
                    ) : (
                      <span className="text-2xl font-bold text-white mr-1">
                        {getMinutesAmount(p)}
                      </span>
                    )}
                    {t("common.minutes")}
                  </p>

                  {discount !== 0 ? (
                    <div>
                      <p className="flex flex-row items-center text-c-gray-400 line-through">
                        {formatObject(getPriceRub(p), "priceRub")}
                      </p>
                      <p className="flex flex-row items-center text-gray-300">
                        {formatObject(getPriceRub(p, discount), "priceRub")}
                      </p>
                    </div>
                  ) : (
                    <p className="flex flex-row items-center text-gray-300">
                      {formatObject(getPriceRub(p), "priceRub")}
                    </p>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={() => clickMinus(idx)}
                      className="text-gray-300 bg-c-gray-600 p-1 rounded-lg"
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                    <p
                      className="flex flex-row items-center font-bold"
                      id={`package_${idx}_amount`}
                    >
                      {packagesCounter[idx] ? packagesCounter[idx].value : 0}
                    </p>
                    <button
                      onClick={() => clickPlus(idx)}
                      className="text-gray-300 bg-c-gray-600 p-1 rounded-lg"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <div className="">
        <CyberpassButton onClick={handleConfirm} type="submit">
          <p className="mx-6">{t("common.confirm")}</p>
        </CyberpassButton>
      </div>
    </div>
  );
}
export default ChooseMinutesPackage;
